import type { SectionType } from "../types";
import type { GenerationResult } from "./generate";
import type { SurfaceDepth, SurfaceTrigger } from "./surface-spec";

/**
 * Client-side remote generator — the browser's link to the server generate
 * endpoint. The server owns the model call and cache; the client POSTs context
 * and renders the returned spec. DOM-free (only `fetch`), so it stays in the
 * browser bundle without pulling in the server module.
 */

export interface RemoteGenerateInput {
  store: string;
  query: string;
  /** Prior turns in this conversation, so a follow-up is generated with the thread. */
  history?: Array<{ query: string; summary: string }>;
  intent: { cluster: string; score: number };
  entry?: SurfaceTrigger["entry"];
  depth?: SurfaceDepth;
  sectionAnchor?: SectionType;
  source?: SurfaceTrigger["source"];
  product?: { ref: string; title: string; price?: number; image?: string };
  anonymousId?: string;
  cartSubtotal?: number;
  cartItems?: Array<{ title: string; qty?: number }>;
  stylePackRef?: string;
  cold?: boolean;
}

export type RemoteSurfaceGenerator = (input: RemoteGenerateInput) => Promise<GenerationResult>;

export function createRemoteSurfaceGenerator(endpoint: string, init?: RequestInit): RemoteSurfaceGenerator {
  return async (input) => {
    let response: { ok: boolean; status: number; json: () => Promise<unknown> };
    try {
      response = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
        ...init,
      });
    } catch (error) {
      return fail(`fetch_failed: ${message(error)}`);
    }
    if (!response.ok) return fail(`http_${response.status}`);

    let data: { ok?: boolean; spec?: unknown; issues?: unknown };
    try {
      data = (await response.json()) as { ok?: boolean; spec?: unknown; issues?: unknown };
    } catch (error) {
      return fail(`bad_json: ${message(error)}`);
    }

    const issues = Array.isArray(data.issues) ? (data.issues as string[]) : [];
    if (data.ok && data.spec) {
      return { ok: true, spec: data.spec as GenerationResult["spec"], validation: null, issues };
    }
    return { ok: false, spec: null, validation: null, issues };
  };
}

function fail(reason: string): GenerationResult {
  return { ok: false, spec: null, validation: null, issues: [reason] };
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Proven-converting questions for a store — what to invite the shopper to ask.
 * Derived from the generate endpoint (`…/surface/generate` → `…/surface/chips`) so a store
 * configures one URL. Bounded by a timeout and swallowing every failure: proven chips are an
 * upgrade on the heuristic ones, never a dependency, so a slow or missing endpoint must not
 * delay or block the chip row.
 */
export async function fetchProvenQuestions(
  generateEndpoint: string,
  store: string,
  options: { limit?: number; timeoutMs?: number } = {},
): Promise<string[] | null> {
  const base = generateEndpoint.replace(/\/generate\/?$/, "/chips");
  if (base === generateEndpoint || !store) return [];
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), options.timeoutMs ?? 800) : null;
  try {
    const url = `${base}?store=${encodeURIComponent(store)}&limit=${options.limit ?? 4}`;
    const response = await fetch(url, { ...(controller ? { signal: controller.signal } : {}) });
    if (!response.ok) return [];
    const body = (await response.json()) as { questions?: unknown; enabled?: unknown };
    // The merchant switched GemSales off — report it as "no entry point" so the caller mounts
    // nothing, rather than offering a chip that opens onto a refusal.
    if (body.enabled === false) return null;
    if (!Array.isArray(body.questions)) return [];
    return body.questions.filter((q): q is string => typeof q === "string" && q.trim().length > 0).slice(0, 6);
  } catch {
    return [];
  } finally {
    if (timer) clearTimeout(timer);
  }
}
